import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Circle, Group, Image as KonvaImage, Layer, Rect, Stage, Text } from 'react-konva'
import type { Coordinate, DragRect, Problem, QueBoardFeatures } from './types'
import { DEFAULT_FEATURES } from './types'
import {
  MAP_SIZE, MAP_OFFSET_X, MAP_OFFSET_Y, BOUNDARY_GAP, CELL_SIZE, CELL_GAP, SIDE_SIZE,
  BANK_OFFSET_X, BANK_OFFSET_Y, BANK_COLS, BANK_ROWS,
  STAGE_HEIGHT, STAGE_WIDTH,
  LEFT_PANEL_TOTAL,
  TUTORIAL_OFFSET_X, TUTORIAL_OFFSET_Y, TUTORIAL_WIDTH,
  COLORS, CHARACTER_THEMES,
} from './constants'
import { cellToPixel } from './components/Grid'
import { useFontLoader } from './hooks/useFontLoader'
import { useGridState } from './hooks/useGridState'
import { useSideIndicators } from './hooks/useSideIndicators'
import { useDragMode } from './hooks/useDragMode'
import { useAnswerCheck } from './hooks/useAnswerCheck'
import { useImageLoader } from './hooks/useImageLoader'
import { useSound } from './hooks/useSound'
import { useTeleport } from './hooks/useTeleport'
import { Grid } from './components/Grid'
import { Bank } from './components/Bank'
import { SideIndicators } from './components/SideIndicators'
import { ProblemDisplay } from './components/ProblemDisplay'
import { DragOverlay } from './components/DragOverlay'
import { TutorialHighlight, TutorialPanel } from './components/TutorialOverlay'
import { TeleportGhost } from './components/TeleportGhost'
import { useTutorial } from './hooks/useTutorial'
import type { QueBoardProps, TutorialValidationState, TutorialSetupActions } from './types'

const BOUNCE_DURATION = 400
const ANSWER_ANIM_INTERVAL = 500

/** 표시등 x-side 좌표 (SideIndicators와 동일) */
function xSidePos(i: number) {
  const boundaryX = i >= 5 ? BOUNDARY_GAP : 0
  return MAP_OFFSET_X + i * (CELL_SIZE + CELL_GAP) + boundaryX + (CELL_SIZE - SIDE_SIZE) / 2
}

/** 표시등 y-side 좌표 (SideIndicators와 동일) */
function ySidePos(i: number) {
  const boundaryY = i >= 5 ? BOUNDARY_GAP : 0
  return MAP_OFFSET_Y + i * (CELL_SIZE + CELL_GAP) + boundaryY + (CELL_SIZE - SIDE_SIZE) / 2
}

interface BounceItem {
  type: 'side' | 'unit'
  x: number
  y: number
  size: number
}

/** 레거시 깨구단2 스타일 바운스 (scale 1→1.5→0.9→1, 400ms) */
const BounceEffect: React.FC<{
  x: number
  y: number
  size: number
  children: React.ReactNode
}> = ({ x, y, size, children }) => {
  const groupRef = useRef<any>(null)
  const startRef = useRef(performance.now())

  useEffect(() => {
    startRef.current = performance.now()
    let frameId: number

    const animate = () => {
      const elapsed = performance.now() - startRef.current
      let s: number

      if (elapsed < 100) {
        const t = elapsed / 100
        s = 1 + 0.5 * (1 - (1 - t) ** 3)
      } else if (elapsed < 300) {
        const t = (elapsed - 100) / 200
        s = 1.5 - 0.6 * (t ** 3)
      } else if (elapsed < BOUNCE_DURATION) {
        const t = (elapsed - 300) / 100
        s = 0.9 + 0.1 * (1 - (1 - t) ** 3)
      } else {
        return
      }

      const group = groupRef.current
      if (!group) return
      group.scaleX(s)
      group.scaleY(s)
      group.getLayer()?.batchDraw()

      frameId = requestAnimationFrame(animate)
    }

    frameId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(frameId)
  }, [])

  const half = size / 2

  return (
    <Group ref={groupRef} x={x + half} y={y + half} listening={false}>
      <Group x={-half} y={-half}>
        {children}
      </Group>
    </Group>
  )
}

export const QueBoard: React.FC<QueBoardProps> = ({
  isOpen,
  onClose: _onClose,
  problem: externalProblem,
  allowCustomInput,
  onComplete,
  width,
  height,
  config,
  tutorialModules,
  noTopRadius,
  resetKey,
}) => {
  // CookieRun 폰트 로드
  useFontLoader()

  // Config 기반 파생 값 (config 우선, 없으면 개별 props fallback)
  const effectiveProblem = config?.problem ?? externalProblem
  const effectiveAllowCustom = config?.allowCustomInput ?? allowCustomInput
  const features: QueBoardFeatures = config?.features ?? DEFAULT_FEATURES
  const resolvedCharacter = useMemo(() => {
    const c = config?.character ?? 'ddami'
    if (c === 'random') {
      const chars = ['queda', 'chaeli', 'ddami'] as const
      return chars[Math.floor(Math.random() * chars.length)]
    }
    return c
  }, [config?.character])
  const theme = CHARACTER_THEMES[resolvedCharacter]

  // Problem state
  const [problem, setProblem] = useState<Problem>(effectiveProblem ?? { left: 3, right: 5 })
  const [feedback, setFeedback] = useState<{ message: string; isCorrect: boolean } | null>(null)
  const feedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [completed, setCompleted] = useState(false)

  // 토스트 자동 닫기 (정답 메시지는 유지, 나머지는 3초 후 닫힘)
  useEffect(() => {
    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current)
    if (!feedback || (feedback.isCorrect && completed)) return
    feedbackTimerRef.current = setTimeout(() => setFeedback(null), 3000)
    return () => { if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current) }
  }, [feedback, completed])
  const [pendingBankUnits, setPendingBankUnits] = useState(0)
  const [bankSelection, setBankSelection] = useState<{ rect: DragRect; count: number } | null>(null)

  // Answer animation state (표시등 바운스 → 낱개 바운스 → 정답)
  const [answerAnimActive, setAnswerAnimActive] = useState(false)
  const [activeBounceIds, setActiveBounceIds] = useState<number[]>([])
  const [answerMergedBounce, setAnswerMergedBounce] = useState<BounceItem | null>(null)
  const answerAnimItemsRef = useRef<BounceItem[]>([])
  const answerAnimTimersRef = useRef<ReturnType<typeof setTimeout>[]>([])

  // 카운팅 애니메이션 상태
  const [animTens, setAnimTens] = useState<number | null>(null)
  const [animOnes, setAnimOnes] = useState<number | null>(null)
  const [answerBounceCount, setAnswerBounceCount] = useState<number | null>(null)
  const [answerBundleCenter, setAnswerBundleCenter] = useState<{x: number, y: number} | null>(null)
  const [looseUnitNumbers, setLooseUnitNumbers] = useState<Map<number, number>>(new Map())

  const clearAnswerAnimTimers = useCallback(() => {
    for (const t of answerAnimTimersRef.current) clearTimeout(t)
    answerAnimTimersRef.current = []
  }, [])

  // Custom input state
  const showCustomInput = features.customInput || effectiveAllowCustom || !effectiveProblem

  // Hooks
  const grid = useGridState()
  const sides = useSideIndicators()
  const drag = useDragMode()
  const { check } = useAnswerCheck()
  const { images, loaded: imagesLoaded } = useImageLoader(resolvedCharacter)
  const sound = useSound()
  const stageRef = useRef<any>(null)

  // Teleport hook
  // 텔레포트 취소 시 문제 리셋 콜백 (handleReset과 순환 의존 방지)
  const teleportCancelReset = useCallback(() => {
    if (problem) {
      grid.showProblem(problem)
      sides.resetSides()
    }
  }, [problem, grid, sides])

  const teleport = useTeleport(
    grid.mapGrid,
    (updater) => {
      grid.setMapGridDirect(updater)
    },
    setFeedback,
    teleportCancelReset,
  )

  // Initialize/reset problem when config changes or resetKey increments
  const prevResetKeyRef = useRef(resetKey)
  useEffect(() => {
    const isResetKeyChange = resetKey !== prevResetKeyRef.current
    prevResetKeyRef.current = resetKey

    if (effectiveProblem) {
      setProblem(effectiveProblem)
      grid.showProblem(effectiveProblem)
      sides.resetSides()
      teleport.reset()
      clearAnswerAnimTimers()
      setAnswerAnimActive(false)
      setActiveBounceIds([])
      setAnswerMergedBounce(null)
      setAnimTens(null)
      setAnimOnes(null)
      setAnswerBounceCount(null)

      setLooseUnitNumbers(new Map())
      setAutoTieBounce(null)
      setAutoTieBounceCount(null)

      setAutoTieBundleCenter(null)
      setAnswerBundleCenter(null)
      setFeedback(null)
      setCompleted(false)
      setPendingBankUnits(0)
      setBankSelection(null)
      if (isResetKeyChange) {
        autoTieRef.current = false
        setAutoTying(false)
        for (const t of autoTieTimersRef.current) clearTimeout(t)
        autoTieTimersRef.current = []
      }
    }
  }, [effectiveProblem, resetKey])

  // 문제 저장 (편집 완료 시 호출)
  const handleSetProblem = useCallback((left: number, right: number) => {
    const p = { left, right }
    setProblem(p)
    grid.showProblem(p)
    sides.resetSides()
    teleport.reset()
    clearAnswerAnimTimers()
    setAnswerAnimActive(false)
    setActiveBounceIds([])
    setAnswerMergedBounce(null)
    setAnimTens(null)
    setAnimOnes(null)
    setAnswerBounceCount(null)
    setAutoTieBundleCenter(null)
    setAnswerBundleCenter(null)
    setLooseUnitNumbers(new Map())
    setFeedback(null)
    setCompleted(false)
    setPendingBankUnits(0)
    setBankSelection(null)
  }, [grid, sides, teleport, clearAnswerAnimTimers])

  // --- 은행 빌림 상태: bank에 select 또는 off 칸이 있으면 빌림 ---
  const hasBorrowedUnits = useMemo(() => {
    for (let col = 0; col < BANK_COLS; col++) {
      for (let row = 0; row < BANK_ROWS; row++) {
        if (grid.bankGrid[col]?.[row]?.isEmpty) continue
        const st = grid.bankGrid[col]?.[row]?.state
        if (st === 'off' || st === 'select') return true
      }
    }
    return false
  }, [grid.bankGrid])

  // --- 자동 묶음 (줄십): 한 줄 10개 on이면 순차적으로 done + 표시등 활성 + 바운스 ---
  const [autoTying, setAutoTying] = useState(false)
  const autoTieRef = useRef(false) // 중복 진입 방지용 (동기)
  const autoTieTimersRef = useRef<ReturnType<typeof setTimeout>[]>([])
  const [autoTieBounce, setAutoTieBounce] = useState<BounceItem | null>(null)
  const [autoTieBounceCount, setAutoTieBounceCount] = useState<number | null>(null)
  const [autoTieBundleCenter, setAutoTieBundleCenter] = useState<{x: number, y: number} | null>(null)
  useEffect(() => {
    if (completed || teleport.isActive || autoTieRef.current || !features.tie || hasBorrowedUnits || answerAnimActive) return

    // 줄십 가능한 행/열 수집
    const queue: { type: 'row' | 'col'; index: number }[] = []

    for (let row = 0; row < MAP_SIZE; row++) {
      let allOn = true
      for (let col = 0; col < MAP_SIZE; col++) {
        if (grid.mapGrid[col]?.[row]?.state !== 'on') { allOn = false; break }
      }
      if (allOn) queue.push({ type: 'row', index: row })
    }

    for (let col = 0; col < MAP_SIZE; col++) {
      let allOn = true
      for (let row = 0; row < MAP_SIZE; row++) {
        if (grid.mapGrid[col]?.[row]?.state !== 'on') { allOn = false; break }
      }
      if (allOn) queue.push({ type: 'col', index: col })
    }

    if (queue.length === 0) return

    autoTieRef.current = true
    setAutoTying(true)
    // 이전 타이머 정리
    for (const t of autoTieTimersRef.current) clearTimeout(t)
    autoTieTimersRef.current = []

    // 순차 처리: 500ms 간격으로 한 줄씩 (묶기 + 카운팅 바운스 통합)
    queue.forEach((item, i) => {
      const t = setTimeout(() => {
        // 먼저 grid + 표시등 상태 업데이트
        if (item.type === 'row') {
          grid.setRangeState('map', 0, item.index, MAP_SIZE - 1, item.index, 'done')
          sides.tieGroup('y', item.index, 10)
        } else {
          grid.setRangeState('map', item.index, 0, item.index, MAP_SIZE - 1, 'done')
          sides.tieGroup('x', item.index, 10)
        }
        sound.play('tie')

        // 카운팅 바운스: 숫자만 표시 + 그리드 중앙에 "십" 텍스트
        const count = i + 1
        requestAnimationFrame(() => {
          const bounceItem: BounceItem = item.type === 'row'
            ? { type: 'side', x: MAP_OFFSET_X - SIDE_SIZE - 6, y: ySidePos(item.index), size: SIDE_SIZE }
            : { type: 'side', x: xSidePos(item.index), y: MAP_OFFSET_Y - SIDE_SIZE - 6, size: SIDE_SIZE }
          setAutoTieBounce(bounceItem)
          setAutoTieBounceCount(count)
          // 정답 영역 십의자리 동기화
          setAnimTens(count)

          // 묶인 셀 중앙에 "십" 표시
          if (item.type === 'row') {
            const cx = (cellToPixel(0, item.index).x + cellToPixel(MAP_SIZE - 1, item.index).x + CELL_SIZE) / 2
            const cy = cellToPixel(0, item.index).y + CELL_SIZE / 2
            setAutoTieBundleCenter({ x: cx, y: cy })
          } else {
            const cx = cellToPixel(item.index, 0).x + CELL_SIZE / 2
            const cy = (cellToPixel(item.index, 0).y + cellToPixel(item.index, MAP_SIZE - 1).y + CELL_SIZE) / 2
            setAutoTieBundleCenter({ x: cx, y: cy })
          }

          // BOUNCE_DURATION 후 바운스 제거
          const tBounce = setTimeout(() => {
            setAutoTieBounce(null)
            setAutoTieBounceCount(null)
            setAutoTieBundleCenter(null)
          }, BOUNCE_DURATION)
          autoTieTimersRef.current.push(tBounce)
        })

        // 마지막 줄 처리 후 플래그 해제 (마지막 바운스 완료 대기)
        if (i === queue.length - 1) {
          setTimeout(() => {
            autoTieRef.current = false
            setAutoTying(false)
          }, BOUNCE_DURATION + 100)
        }
      }, 200 + i * 500)
      autoTieTimersRef.current.push(t)
    })
  }, [grid.mapGrid, completed, teleport.isActive])

  // --- Drag handlers for map (항상 묶기 모드) ---
  const handleMapMouseDown = useCallback(
    (coord: Coordinate) => {
      const { col, row } = coord

      // 텔레포트 placing 중 클릭 → 배치 시도
      if (teleport.state.phase === 'placing') {
        if (teleport.state.ghostAnchor && teleport.isPlacementValid(teleport.state.ghostAnchor)) {
          sound.play('move')
          teleport.confirmPlacement(teleport.state.ghostAnchor)
        } else {
          setFeedback({ message: '여기에는 배치할 수 없습니다', isCorrect: false })
          setTimeout(() => setFeedback(null), 1500)
        }
        return
      }

      // 텔레포트 selecting 중 클릭 → 해당 행 선택 (추천 행이면)
      if (teleport.state.phase === 'selecting') {
        const lineIndex = teleport.state.axis === 'row' ? row : col
        teleport.selectLine(lineIndex)
        return
      }

      if (completed || teleport.isActive) return
      setFeedback(null)
      sound.play('select')
      drag.startDrag(coord, 'map')
    },
    [completed, teleport, drag, sound],
  )

  const handleMapMouseEnter = useCallback(
    (coord: Coordinate) => {
      const { col, row } = coord

      // 텔레포트 placing 중 마우스 이동 → 고스트 앵커 업데이트
      if (teleport.state.phase === 'placing') {
        const axis = teleport.state.axis
        if (axis === 'row') {
          teleport.updateGhostAnchor({ col, row: 0 })
        } else {
          teleport.updateGhostAnchor({ col: 0, row })
        }
        return
      }

      // 텔레포트 selecting 중 드래그 → 행 선택 (mouseDown으로 시작된 경우만)
      if (teleport.state.phase === 'selecting' && teleport.state.selectedLines.length > 0) {
        const lineIndex = teleport.state.axis === 'row' ? row : col
        teleport.selectLine(lineIndex)
        return
      }

      if (drag.session.active && drag.session.target === 'map') {
        const prev = drag.session.endCell
        if (!prev || prev.col !== coord.col || prev.row !== coord.row) {
          sound.play('select')
        }
        drag.updateDrag(coord)
      }
    },
    [drag, sound, teleport],
  )

  const handleMapMouseUp = useCallback(
    (_coord: Coordinate) => {
      // 텔레포트 selecting 드래그 종료 → 추천 행 전부 선택 완료시 placing으로 전환
      if (teleport.state.phase === 'selecting') {
        if (teleport.isSelectionComplete) {
          teleport.confirmSelection()
        }
        return
      }

      if (!drag.session.active) return
      const session = drag.endDrag()

      if (!session.rect) return
      const { startCol, startRow, endCol, endRow } = session.rect

      // --- 은행에서 빌린 상태 (묶기 비활성) ---
      if (hasBorrowedUnits && features.bank) {
        if (pendingBankUnits > 0) {
          // 놓기 모드: 빌린 인형을 보드에 배치
          const placed = grid.placeOnMap(startCol, startRow, endCol, endRow, pendingBankUnits)
          if (placed > 0) {
            grid.confirmBorrow(placed) // 은행 'select' → 'off' (사라짐)
            sound.play('select')
            const remaining = pendingBankUnits - placed
            setPendingBankUnits(remaining)
            if (remaining <= 0) setBankSelection(null)
            setFeedback({ message: `${placed}개를 보드에 놓았습니다`, isCorrect: true })
          } else {
            sound.play('wrong')
            setFeedback({ message: '빈 칸(인형이 없는 칸)에 놓아주세요', isCorrect: false })
          }
        } else {
          // 반납 모드: 보드의 인형을 은행에 반납
          const collected = grid.collectFromMap(startCol, startRow, endCol, endRow, grid.bankBorrowedCount)
          if (collected > 0) {
            grid.returnToBank(collected)
            sound.play('select')
            setFeedback({ message: `${collected}개를 은행에 반납했습니다`, isCorrect: true })
          } else {
            setFeedback({ message: '인형이 있는 칸을 선택하여 은행에 반납하세요', isCorrect: false })
          }
        }
        return
      }

      // --- 묶기 모드 ---
      const colSpan = endCol - startCol + 1
      const rowSpan = endRow - startRow + 1

      // 범위 내 모든 인형이 on이어야 함
      let allOn = true
      for (let c = startCol; c <= endCol; c++) {
        for (let r = startRow; r <= endRow; r++) {
          if (grid.mapGrid[c]?.[r]?.state !== 'on') { allOn = false; break }
        }
        if (!allOn) break
      }
      if (!allOn) return

      // 총 개수가 10이어야 묶기 가능 (줄십: 1×10/10×1, 뚱십: 2×5/5×2)
      const totalCells = colSpan * rowSpan
      if (totalCells !== 10) {
        if (totalCells >= 2) {
          sound.play('wrong')
          setFeedback({ message: '10개씩만 묶을 수 있습니다', isCorrect: false })
        }
        return
      }

      // 인형을 done으로
      sound.play('tie')
      grid.setRangeState('map', startCol, startRow, endCol, endRow, 'done')

      // 표시등 활성화
      if (colSpan === 10 && rowSpan === 1) {
        // 줄십 가로 (1×10) → 좌측(y) 표시등
        sides.tieGroup('y', startRow, 10)
      } else if (rowSpan === 10 && colSpan === 1) {
        // 줄십 세로 (10×1) → 상단(x) 표시등
        sides.tieGroup('x', startCol, 10)
      } else if (colSpan < 10 && rowSpan < 10) {
        // 뚱십 (2×5, 5×2) → 짧은 쪽 표시등 합쳐서 "십" 표시
        const mapRange = { startCol, startRow, endCol, endRow }
        if (colSpan <= rowSpan) {
          // colSpan이 짧음 → x-side (상단) 표시등 합치기
          const indices = Array.from({ length: colSpan }, (_, i) => startCol + i)
          sides.tieMergedGroup('x', indices, mapRange)
        } else {
          // rowSpan이 짧음 → y-side (좌측) 표시등 합치기
          const indices = Array.from({ length: rowSpan }, (_, i) => startRow + i)
          sides.tieMergedGroup('y', indices, mapRange)
        }
      }
    },
    [drag, grid, sides, hasBorrowedUnits, pendingBankUnits, features.bank, sound, teleport],
  )

  // --- Bank drag handlers (은행에서 빌리기) ---
  const handleBankMouseDown = useCallback(
    (coord: Coordinate) => {
      if (completed || teleport.isActive) return
      setFeedback(null)
      sound.play('select')
      drag.startDrag(coord, 'bank')
    },
    [completed, teleport.isActive, drag, sound],
  )

  const handleBankMouseEnter = useCallback(
    (coord: Coordinate) => {
      if (drag.session.active && drag.session.target === 'bank') {
        const prev = drag.session.endCell
        if (!prev || prev.col !== coord.col || prev.row !== coord.row) {
          sound.play('select')
        }
        drag.updateDrag(coord)
      }
    },
    [drag, sound],
  )

  const handleBankMouseUp = useCallback(
    (_coord: Coordinate) => {
      if (!drag.session.active || drag.session.target !== 'bank') return
      const session = drag.endDrag()
      if (!session.rect) return

      const { startCol, startRow, endCol, endRow } = session.rect
      const borrowed = grid.borrowFromBank(startCol, startRow, endCol, endRow)
      if (borrowed > 0) {
        sound.play('select')
        setPendingBankUnits(prev => prev + borrowed)
        setBankSelection({ rect: session.rect!, count: borrowed })
        setFeedback({ message: `${borrowed}개를 은행에서 꺼냈습니다. 보드에 놓아주세요.`, isCorrect: true })
      }
    },
    [drag, grid, sound],
  )

  // --- Touch drag support (tablets) ---
  const pixelToMapCell = useCallback((px: number, py: number): Coordinate | null => {
    let relX = px - MAP_OFFSET_X
    let relY = py - MAP_OFFSET_Y
    if (relX < 0 || relY < 0) return null
    if (relX >= 5 * (CELL_SIZE + CELL_GAP)) relX -= BOUNDARY_GAP
    if (relY >= 5 * (CELL_SIZE + CELL_GAP)) relY -= BOUNDARY_GAP
    const col = Math.floor(relX / (CELL_SIZE + CELL_GAP))
    const row = Math.floor(relY / (CELL_SIZE + CELL_GAP))
    if (col < 0 || col >= MAP_SIZE || row < 0 || row >= MAP_SIZE) return null
    return { col, row }
  }, [])

  const pixelToBankCell = useCallback((px: number, py: number): Coordinate | null => {
    const relX = px - BANK_OFFSET_X - 100
    const relY = py - BANK_OFFSET_Y - 25
    if (relX < 0 || relY < 0) return null
    const col = Math.floor(relX / (CELL_SIZE + CELL_GAP))
    const row = Math.floor(relY / (CELL_SIZE + CELL_GAP))
    if (col < 0 || col >= BANK_COLS || row < 0 || row >= BANK_ROWS) return null
    return { col, row }
  }, [])

  const handleStageMouseMove = useCallback((e: any) => {
    if (teleport.state.phase !== 'placing') return
    const stage = e.target.getStage()
    const pos = stage.getPointerPosition()
    if (!pos) return
    const cell = pixelToMapCell(pos.x, pos.y)
    if (!cell) {
      teleport.updateGhostAnchor(null)
      return
    }
    const axis = teleport.state.axis
    if (axis === 'row') {
      teleport.updateGhostAnchor({ col: cell.col, row: 0 })
    } else {
      teleport.updateGhostAnchor({ col: 0, row: cell.row })
    }
  }, [teleport, pixelToMapCell])

  const handleStageTouchMove = useCallback(
    (e: any) => {
      // 텔레포트 placing 중 터치 이동 → 고스트 앵커 업데이트
      if (teleport.state.phase === 'placing') {
        const stage = e.target.getStage()
        const pos = stage.getPointerPosition()
        if (!pos) return
        const cell = pixelToMapCell(pos.x, pos.y)
        if (!cell) {
          teleport.updateGhostAnchor(null)
          return
        }
        const axis = teleport.state.axis
        if (axis === 'row') {
          teleport.updateGhostAnchor({ col: cell.col, row: 0 })
        } else {
          teleport.updateGhostAnchor({ col: 0, row: cell.row })
        }
        return
      }

      if (!drag.session.active) return
      e.evt.preventDefault()
      const stage = stageRef.current
      if (!stage) return
      const pos = stage.getPointerPosition()
      if (!pos) return

      const target = drag.session.target
      if (target === 'map') {
        const cell = pixelToMapCell(pos.x, pos.y)
        if (cell) handleMapMouseEnter(cell)
      } else if (target === 'bank') {
        const cell = pixelToBankCell(pos.x, pos.y)
        if (cell) handleBankMouseEnter(cell)
      }
    },
    [drag.session.active, drag.session.target, pixelToMapCell, pixelToBankCell, handleMapMouseEnter, handleBankMouseEnter, teleport],
  )

  // --- Teleport cancel (ESC key) ---
  useEffect(() => {
    if (teleport.state.phase === 'idle') return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') teleport.cancel()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [teleport.state.phase, teleport])

  // --- Teleport handler ---
  const handleTeleport = useCallback((targetLength: number) => {
    if (completed || teleport.isActive) return
    sound.play('click')
    teleport.startSelecting(targetLength)
  }, [completed, teleport, sound])

  // --- 뚱십 풀기 handler ---
  const handleReleaseMergedGroup = useCallback((groupId: string) => {
    // 현재 mergedGroups에서 대상 그룹을 먼저 찾기
    const group = sides.mergedGroups.find(g => g.id === groupId)
    if (!group) return

    // 표시등 상태 복원 (십 → 개별 번호) + mergedGroups에서 제거
    sides.releaseMergedGroup(groupId, group)

    // 맵 그리드 done → on 복원
    if (group.mapRange) {
      grid.untieMergedRange(
        group.mapRange.startCol,
        group.mapRange.startRow,
        group.mapRange.endCol,
        group.mapRange.endRow,
      )
    }

    // 정답 상태 리셋 (정답 조건이 깨지므로)
    clearAnswerAnimTimers()
    setAnswerAnimActive(false)
    setActiveBounceIds([])
    setAnswerMergedBounce(null)
    setAnimTens(null)
    setAnimOnes(null)
    setAnswerBounceCount(null)
    setAutoTieBundleCenter(null)
    setAnswerBundleCenter(null)
    setLooseUnitNumbers(new Map())
    setCompleted(false)
    setFeedback(null)
    sound.play('select')
  }, [sides.mergedGroups, sides, grid, sound, clearAnswerAnimTimers])

  const startAnswerAnimation = useCallback((expected: number) => {
    clearAnswerAnimTimers()

    const RIGHT_ANIM_DURATION = 1500

    // --- 십묶음 수집: 뚱십(mergedGroups)만 수집 (줄십은 autoTie에서 처리됨) ---
    const tenItems: BounceItem[] = sides.mergedGroups.map(g => {
      const sorted = [...g.indices].sort((a, b) => a - b)
      const first = sorted[0]
      const last = sorted[sorted.length - 1]
      let x: number, y: number
      if (g.dir === 'x') {
        const firstX = xSidePos(first)
        const lastX = xSidePos(last) + SIDE_SIZE
        x = (firstX + lastX) / 2 - SIDE_SIZE / 2
        y = MAP_OFFSET_Y - SIDE_SIZE - 6
      } else {
        x = MAP_OFFSET_X - SIDE_SIZE - 6
        const firstY = ySidePos(first)
        const lastY = ySidePos(last) + SIDE_SIZE
        y = (firstY + lastY) / 2 - SIDE_SIZE / 2
      }
      return { type: 'side' as const, x, y, size: SIDE_SIZE }
    })

    const unitItems: BounceItem[] = grid.looseUnits.map(u => {
      const pos = cellToPixel(u.col, u.row)
      return { type: 'unit' as const, x: pos.x, y: pos.y, size: CELL_SIZE }
    })

    if (tenItems.length === 0 && unitItems.length === 0) {
      sound.play('right')
      setFeedback(null)
      setCompleted(true)
      const tRight = setTimeout(() => {
        onComplete?.({ problem: [problem.left, problem.right], answer: expected })
      }, RIGHT_ANIM_DURATION)
      answerAnimTimersRef.current.push(tRight)
      return
    }

    answerAnimItemsRef.current = unitItems
    setAnswerAnimActive(true)
    setActiveBounceIds([])
    setAnimTens(null)
    setAnimOnes(null)
    setAnswerBounceCount(null)
    setAnswerBundleCenter(null)
    setLooseUnitNumbers(new Map())

    // --- 십묶음 카운팅 바운스 (500ms 간격, 초기 200ms 대기) ---
    let delay = 200
    for (let i = 0; i < tenItems.length; i++) {
      const item = tenItems[i]
      const count = i + 1
      const t = setTimeout(() => {
        sound.play('tie')
        setAnswerMergedBounce(item)
        setAnswerBounceCount(count)
        setAnimTens(count)

        // 뚱십 그룹의 done 셀 중앙에 "십" 표시
        const g = sides.mergedGroups[i]
        if (g) {
          const doneCells: {col: number, row: number}[] = []
          if (g.dir === 'x') {
            for (const colIdx of g.indices) {
              for (let r = 0; r < 10; r++) {
                if (grid.mapGrid[colIdx]?.[r]?.state === 'done') doneCells.push({col: colIdx, row: r})
              }
            }
          } else {
            for (const rowIdx of g.indices) {
              for (let c = 0; c < 10; c++) {
                if (grid.mapGrid[c]?.[rowIdx]?.state === 'done') doneCells.push({col: c, row: rowIdx})
              }
            }
          }
          if (doneCells.length > 0) {
            const cols = doneCells.map(c => c.col)
            const rows = doneCells.map(c => c.row)
            const minC = Math.min(...cols), maxC = Math.max(...cols)
            const minR = Math.min(...rows), maxR = Math.max(...rows)
            const cx = (cellToPixel(minC, minR).x + cellToPixel(maxC, minR).x + CELL_SIZE) / 2
            const cy = (cellToPixel(minC, minR).y + cellToPixel(minC, maxR).y + CELL_SIZE) / 2
            setAnswerBundleCenter({x: cx, y: cy})
          }
        }

        const tRemove = setTimeout(() => {
          setAnswerMergedBounce(null)
          setAnswerBounceCount(null)
          setAnswerBundleCenter(null)
        }, BOUNCE_DURATION)
        answerAnimTimersRef.current.push(tRemove)
      }, delay)
      answerAnimTimersRef.current.push(t)
      delay += ANSWER_ANIM_INTERVAL
    }

    const unitStartDelay = tenItems.length > 0
      ? delay - ANSWER_ANIM_INTERVAL + BOUNCE_DURATION + 100
      : 0

    if (unitItems.length === 0) {
      const tFinal = setTimeout(() => {
        setAnswerMergedBounce(null)
        setAnswerBounceCount(null)
  
        setAnswerBundleCenter(null)
        setAnswerAnimActive(false)
        sound.play('right')
        setFeedback(null)
        setCompleted(true)
        const tRight = setTimeout(() => {
          onComplete?.({ problem: [problem.left, problem.right], answer: expected })
        }, RIGHT_ANIM_DURATION)
        answerAnimTimersRef.current.push(tRight)
      }, unitStartDelay)
      answerAnimTimersRef.current.push(tFinal)
      return
    }

    // --- 낱개 카운팅 (500ms 간격) ---
    let unitDelay = unitStartDelay
    for (let i = 0; i < unitItems.length; i++) {
      const idx = i
      const count = i + 1
      const t = setTimeout(() => {
        setActiveBounceIds(prev => [...prev, idx])
        sound.play('select')
        setLooseUnitNumbers(prev => new Map(prev).set(idx, count))
        setAnimOnes(count)

        const tRemove = setTimeout(() => {
          setActiveBounceIds(prev => prev.filter(id => id !== idx))
        }, BOUNCE_DURATION)
        answerAnimTimersRef.current.push(tRemove)
      }, unitDelay)
      answerAnimTimersRef.current.push(t)
      unitDelay += ANSWER_ANIM_INTERVAL
    }

    const finalDelay = unitDelay - ANSWER_ANIM_INTERVAL + BOUNCE_DURATION
    const tFinal = setTimeout(() => {
      setActiveBounceIds([])
      setAnswerMergedBounce(null)
      setAnswerBounceCount(null)

      setAnswerBundleCenter(null)
      setAnswerAnimActive(false)
      sound.play('right')
      setFeedback(null)
      setCompleted(true)
      const tRight = setTimeout(() => {
        onComplete?.({ problem: [problem.left, problem.right], answer: expected })
      }, RIGHT_ANIM_DURATION)
      answerAnimTimersRef.current.push(tRight)
    }, finalDelay)
    answerAnimTimersRef.current.push(tFinal)
  }, [sides, grid, sound, problem, onComplete, clearAnswerAnimTimers])

  // --- 자동 정답 판정: 묶기/상태 변경 시 조건 충족하면 자동 완료 ---
  useEffect(() => {
    if (completed || teleport.isActive || autoTying || answerAnimActive) return
    // 묶음이 하나도 없으면 체크 불필요
    if (grid.doneCount === 0) return

    const result = check({
      problem,
      looseUnits: grid.looseUnits,
      doneCount: grid.doneCount,
      isBankFull: grid.isBankFull,
    })

    if (result.isCorrect) {
      startAnswerAnimation(result.expected)
    }
  }, [grid.doneCount, grid.looseUnits.length, grid.isBankFull, completed, teleport.isActive, autoTying, answerAnimActive])

  // --- Reset ---
  const handleReset = useCallback(() => {
    grid.showProblem(problem)
    sides.resetSides()
    teleport.reset()
    clearAnswerAnimTimers()
    setAnswerAnimActive(false)
    setActiveBounceIds([])
    setAnswerMergedBounce(null)
    setAnimTens(null)
    setAnimOnes(null)
    setAnswerBounceCount(null)
    setAutoTieBundleCenter(null)
    setAnswerBundleCenter(null)
    setLooseUnitNumbers(new Map())
    setFeedback(null)
    setCompleted(false)
    setPendingBankUnits(0)
    setBankSelection(null)
  }, [grid, sides, teleport, problem, clearAnswerAnimTimers])

  // --- Computed values ---
  // 현재 드래그 모드 (시각 피드백용)
  const effectiveDragMode = (() => {
    if (!drag.session.active) return 'tie' as const
    if (drag.session.target === 'bank') return 'borrow' as const
    if (hasBorrowedUnits && pendingBankUnits > 0) return 'place' as const
    if (hasBorrowedUnits && pendingBankUnits === 0) return 'return' as const
    return 'tie' as const
  })()

  // 드래그 중 셀 개수 계산
  const dragCellCount = (() => {
    const s = drag.session
    if (!s.active || !s.rect || !s.target || s.target === 'side') return 0
    const { startCol, startRow, endCol, endRow } = s.rect

    if (s.target === 'bank') {
      // 은행: 빌릴 수 있는 'on' 셀 수
      let count = 0
      for (let c = startCol; c <= endCol; c++) {
        for (let r = startRow; r <= endRow; r++) {
          if (grid.bankGrid[c]?.[r] && !grid.bankGrid[c][r].isEmpty && grid.bankGrid[c][r].state === 'on') count++
        }
      }
      return count
    }

    if (s.target === 'map') {
      if (hasBorrowedUnits && pendingBankUnits > 0) {
        // 놓기 모드: 배치 가능한 'off' 셀 수 (pending 한도)
        let count = 0
        for (let c = startCol; c <= endCol; c++) {
          for (let r = startRow; r <= endRow; r++) {
            if (grid.mapGrid[c]?.[r]?.state === 'off') count++
          }
        }
        return Math.min(count, pendingBankUnits)
      } else if (hasBorrowedUnits) {
        // 반납 모드: 반납 가능한 'on' 셀 수
        let count = 0
        for (let c = startCol; c <= endCol; c++) {
          for (let r = startRow; r <= endRow; r++) {
            if (grid.mapGrid[c]?.[r]?.state === 'on') count++
          }
        }
        return Math.min(count, grid.bankBorrowedCount)
      } else {
        // 묶기 모드: on 셀 수
        let count = 0
        for (let c = startCol; c <= endCol; c++) {
          for (let r = startRow; r <= endRow; r++) {
            const st = grid.mapGrid[c]?.[r]?.state
            if (st === 'on' || st === 'off') count++
          }
        }
        return count
      }
    }
    return 0
  })()

  // --- Tutorial ---
  const tutorialValidationState = useMemo<TutorialValidationState>(() => ({
    mapGrid: grid.mapGrid,
    bankGrid: grid.bankGrid,
    xSides: sides.xSides,
    ySides: sides.ySides,
    fullCount: grid.doneCount,
    looseUnits: grid.looseUnits,
    currentMode: 'tie',
    completed,
  }), [grid.mapGrid, grid.bankGrid, sides.xSides, sides.ySides, grid.doneCount, grid.looseUnits, completed])

  const tutorialSetupActions = useMemo<TutorialSetupActions>(() => ({
    showProblem: (p) => { setProblem(p); grid.showProblem(p) },
    resetSides: () => sides.resetSides(),
    setUnitState: (container, col, row, state) => grid.setUnitState(container, col, row, state),
    setRangeState: (container, sc, sr, ec, er, state) => grid.setRangeState(container, sc, sr, ec, er, state),
    tieGroup: (dir, index, size) => sides.tieGroup(dir, index, size),
    mergeSides: () => {}, // No-op: merge 기능 제거됨
  }), [grid, sides])

  const tutorial = useTutorial({
    modules: tutorialModules ?? [],
    enabled: config?.tutorial?.enabled ?? false,
    autoStart: config?.tutorial?.autoStart ?? false,
    validationState: tutorialValidationState,
    setupActions: tutorialSetupActions,
  })

  const baseWidth = width ?? STAGE_WIDTH
  const baseHeight = height ?? STAGE_HEIGHT

  // 반응형: 컨테이너 너비에 맞춰 scale
  const containerRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)

  useEffect(() => {
    if (!containerRef.current) return
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const containerWidth = entry.contentRect.width
        if (containerWidth > 0 && containerWidth < baseWidth) {
          setScale(containerWidth / baseWidth)
        } else {
          setScale(1)
        }
      }
    })
    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [baseWidth])

  const controlsDisabled = completed || teleport.isActive || answerAnimActive

  // --- 묶음 번호 계산 (표시등에 숫자 표시용) ---
  const bundleNumbers = useMemo(() => {
    const map = new Map<string, number>()
    let count = 0
    // merged group에 속한 인덱스 제외
    const xMerged = new Set<number>()
    const yMerged = new Set<number>()
    for (const g of sides.mergedGroups) {
      const set = g.dir === 'x' ? xMerged : yMerged
      for (const idx of g.indices) set.add(idx)
    }
    sides.xSides.forEach((s, i) => {
      if (s.state === 'full' && !xMerged.has(i)) map.set(`x-${i}`, ++count)
    })
    sides.ySides.forEach((s, i) => {
      if (s.state === 'full' && !yMerged.has(i)) map.set(`y-${i}`, ++count)
    })
    sides.mergedGroups.forEach((g) => {
      map.set(`merged-${g.id}`, ++count)
    })
    return map
  }, [sides.xSides, sides.ySides, sides.mergedGroups])

  // --- done 셀 그룹 (테두리 + 십 센터 렌더링용, 각 묶음 10개 단위) ---
  const doneGroups = useMemo(() => {
    const groups: { minC: number; maxC: number; minR: number; maxR: number }[] = []
    const xMerged = new Set<number>()
    const yMerged = new Set<number>()
    for (const g of sides.mergedGroups) {
      const set = g.dir === 'x' ? xMerged : yMerged
      for (const idx of g.indices) set.add(idx)
    }
    // 줄십: full x-sides (세로 10칸)
    sides.xSides.forEach((s, i) => {
      if (s.state === 'full' && !xMerged.has(i)) {
        let minR = MAP_SIZE, maxR = -1
        for (let r = 0; r < MAP_SIZE; r++) {
          if (grid.mapGrid[i]?.[r]?.state === 'done') {
            if (r < minR) minR = r
            if (r > maxR) maxR = r
          }
        }
        if (maxR >= 0) groups.push({ minC: i, maxC: i, minR, maxR })
      }
    })
    // 줄십: full y-sides (가로 10칸)
    sides.ySides.forEach((s, i) => {
      if (s.state === 'full' && !yMerged.has(i)) {
        let minC = MAP_SIZE, maxC = -1
        for (let c = 0; c < MAP_SIZE; c++) {
          if (grid.mapGrid[c]?.[i]?.state === 'done') {
            if (c < minC) minC = c
            if (c > maxC) maxC = c
          }
        }
        if (maxC >= 0) groups.push({ minC, maxC, minR: i, maxR: i })
      }
    })
    // 뚱십: merged groups — 정확히 10셀에 해당하는 행/열만 취함
    sides.mergedGroups.forEach(g => {
      const sortedIdx = [...g.indices].sort((a, b) => a - b)
      const numIdx = sortedIdx.length
      const targetSize = 10 / numIdx // 2cols → 5rows, 5cols → 2rows
      if (g.dir === 'x') {
        // x-direction: merge된 cols에서 첫 targetSize개 연속 done rows
        let startRow = -1, count = 0
        for (let r = 0; r < MAP_SIZE; r++) {
          if (sortedIdx.every(c => grid.mapGrid[c]?.[r]?.state === 'done')) {
            if (startRow < 0) startRow = r
            count++
            if (count === targetSize) {
              groups.push({ minC: sortedIdx[0], maxC: sortedIdx[numIdx - 1], minR: startRow, maxR: r })
              break
            }
          } else if (startRow >= 0) {
            break // 연속 끊김
          }
        }
      } else {
        // y-direction: merge된 rows에서 첫 targetSize개 연속 done cols
        let startCol = -1, count = 0
        for (let c = 0; c < MAP_SIZE; c++) {
          if (sortedIdx.every(r => grid.mapGrid[c]?.[r]?.state === 'done')) {
            if (startCol < 0) startCol = c
            count++
            if (count === targetSize) {
              groups.push({ minC: startCol, maxC: c, minR: sortedIdx[0], maxR: sortedIdx[numIdx - 1] })
              break
            }
          } else if (startCol >= 0) {
            break
          }
        }
      }
    })
    return groups
  }, [grid.mapGrid, grid.doneCount, sides.xSides, sides.ySides, sides.mergedGroups])

  if (!isOpen) return null

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', width: '100%', maxWidth: baseWidth }}
    >
      <div
        style={{
          position: 'relative',
          width: baseWidth * scale,
          height: baseHeight * scale,
          overflow: 'hidden',
        }}
      >
      <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left', width: baseWidth, height: baseHeight, position: 'absolute', top: 0, left: 0 }}>
      <Stage
        ref={stageRef}
        width={baseWidth}
        height={baseHeight}
        onTouchMove={handleStageTouchMove}
        onMouseMove={handleStageMouseMove}
        onContextMenu={(e: { evt: { preventDefault: () => void } }) => {
          e.evt.preventDefault()
          if (teleport.state.phase !== 'idle') teleport.cancel()
        }}
        style={{
          touchAction: 'none',
          cursor: 'pointer',
        }}
      >
        <Layer>
          {/* === 배경 패널 (좌측 초록 / 브라우 / 우측 캐릭터) === */}
          {/* 좌측 패널: 배경 (#06063F) + 컨테이너 (#19195B) */}
          <Rect x={0} y={0} width={LEFT_PANEL_TOTAL} height={baseHeight} fill={COLORS.leftPanel} cornerRadius={noTopRadius ? [0, 0, 0, 12] : [12, 0, 0, 12]} />
          <Rect x={20} y={20} width={LEFT_PANEL_TOTAL - 40} height={baseHeight - 40} fill={COLORS.problemCard} cornerRadius={10} listening={false} />
          <Rect x={LEFT_PANEL_TOTAL} y={0} width={baseWidth - LEFT_PANEL_TOTAL} height={baseHeight} fill={theme.rightBg} cornerRadius={noTopRadius ? [0, 0, 12, 0] : [0, 12, 12, 0]} />

          {/* Problem display + Answer */}
          <ProblemDisplay
            problem={problem}
            images={imagesLoaded ? images : null}
            doneCount={grid.doneCount}
            looseCount={grid.looseUnits.length}
            completed={completed}
            allowCustomInput={showCustomInput}
            onSaveProblem={handleSetProblem}
            onReset={handleReset}
            animTens={animTens}
            animOnes={animOnes}
          />

          {/* Side indicators (텔레포트 기능 통합) */}
          <SideIndicators
            xSides={sides.xSides}
            ySides={sides.ySides}
            mergedGroups={sides.mergedGroups}
            images={imagesLoaded ? images : null}
            teleport5={features.teleport5}
            teleport10={features.teleport10}
            teleportDisabled={controlsDisabled || grid.doneCount > 0}
            onTeleport={handleTeleport}
            activeTeleport={teleport.state.phase !== 'idle' && teleport.state.axis && teleport.state.targetLength
              ? { axis: teleport.state.axis, targetLength: teleport.state.targetLength }
              : null}
            onTeleportCancel={teleport.cancel}
            onReleaseMergedGroup={handleReleaseMergedGroup}
            bundleNumbers={bundleNumbers}
            disabled={!features.indicator}
          />

          {/* Main grid */}
          <Grid
            grid={grid.mapGrid}
            images={imagesLoaded ? images : null}
            onCellMouseDown={handleMapMouseDown}
            onCellMouseEnter={handleMapMouseEnter}
            onCellMouseUp={handleMapMouseUp}
          />

          {/* 묶음 테두리 + 센터 "십" (done 그룹 상시 표시) */}
          {doneGroups.length > 0 && (
            <Group listening={false}>
              {doneGroups.map((g, i) => {
                const topLeft = cellToPixel(g.minC, g.minR)
                const bottomRight = cellToPixel(g.maxC, g.maxR)
                const w = bottomRight.x - topLeft.x + CELL_SIZE
                const h = bottomRight.y - topLeft.y + CELL_SIZE
                const cx = topLeft.x + w / 2
                const cy = topLeft.y + h / 2
                return (
                  <Group key={`bundle-overlay-${i}`}>
                    <Rect
                      x={topLeft.x - 2}
                      y={topLeft.y - 2}
                      width={w + 4}
                      height={h + 4}
                      stroke="#FF9800"
                      strokeWidth={3}
                      cornerRadius={6}
                    />
                    <Circle
                      x={cx}
                      y={cy}
                      radius={20}
                      fill="rgba(0,0,0,0.5)"
                    />
                    <Text
                      x={cx - 20}
                      y={cy - 20}
                      width={40}
                      height={40}
                      text="십"
                      fontSize={22}
                      fontStyle="bold"
                      fontFamily="CookeiRunBold"
                      fill="#FFFFFF"
                      align="center"
                      verticalAlign="middle"
                    />
                  </Group>
                )
              })}
            </Group>
          )}

          {/* Teleport selecting: 비추천 영역 비활성화 + 선택 완료 행 하이라이트 */}
          {teleport.state.phase === 'selecting' && teleport.state.recommendedCells.length > 0 && (
            <Group listening={false}>
              {/* 비추천 영역 어둡게 (on 상태이지만 추천 아닌 셀) */}
              {(() => {
                const recommendedSet = new Set(
                  teleport.state.recommendedCells.map(({ col, row }) => `${col},${row}`)
                )
                const dimCells: { col: number; row: number }[] = []
                for (let c = 0; c < 10; c++) {
                  for (let r = 0; r < 10; r++) {
                    const st = grid.mapGrid[c]?.[r]?.state
                    if (st === 'on' && !recommendedSet.has(`${c},${r}`)) {
                      dimCells.push({ col: c, row: r })
                    }
                  }
                }
                return dimCells.map(({ col, row }) => {
                  const pos = cellToPixel(col, row)
                  return (
                    <Rect
                      key={`tp-dim-${col}-${row}`}
                      x={pos.x}
                      y={pos.y}
                      width={CELL_SIZE}
                      height={CELL_SIZE}
                      fill="rgba(0,0,0,0.35)"
                      cornerRadius={4}
                    />
                  )
                })
              })()}
              {/* 선택 완료된 행만 하이라이트 */}
              {teleport.state.recommendedCells
                .filter(({ col, row }) => {
                  const lineIndex = teleport.state.axis === 'row' ? row : col
                  return teleport.state.selectedLines.includes(lineIndex)
                })
                .map(({ col, row }) => {
                  const pos = cellToPixel(col, row)
                  return (
                    <Rect
                      key={`tp-sel-${col}-${row}`}
                      x={pos.x}
                      y={pos.y}
                      width={CELL_SIZE}
                      height={CELL_SIZE}
                      fill={COLORS.teleportSelect}
                      cornerRadius={4}
                    />
                  )
                })}
            </Group>
          )}

          {/* Teleport rotating transition */}
          {teleport.state.phase === 'rotating' && (
            <Group listening={false}>
              {teleport.state.selectedCells.map(({ col, row }) => {
                const pos = cellToPixel(col, row)
                return (
                  <Rect
                    key={`tp-rot-${col}-${row}`}
                    x={pos.x}
                    y={pos.y}
                    width={CELL_SIZE}
                    height={CELL_SIZE}
                    fill={COLORS.teleportSelect}
                    cornerRadius={4}
                    opacity={0.8}
                  />
                )
              })}
            </Group>
          )}

          {/* Teleport placing: 선택된 셀 인형 유지 (ghost 미표시 시에만) */}
          {teleport.state.phase === 'placing' && !teleport.state.ghostAnchor && teleport.state.selectedCells.length > 0 && (
            <Group listening={false}>
              {teleport.state.selectedCells.map(({ col, row }) => {
                const pos = cellToPixel(col, row)
                const unitImg = images?.unitOn ?? null
                return (
                  <Group key={`tp-placed-${col}-${row}`}>
                    {unitImg ? (
                      <KonvaImage x={pos.x} y={pos.y} width={CELL_SIZE} height={CELL_SIZE} image={unitImg} listening={false} opacity={0.5} />
                    ) : (
                      <Rect x={pos.x} y={pos.y} width={CELL_SIZE} height={CELL_SIZE} fill={COLORS.unitOn} cornerRadius={4} opacity={0.5} />
                    )}
                  </Group>
                )
              })}
            </Group>
          )}

          {/* Teleport placing ghost */}
          {teleport.state.phase === 'placing' && teleport.state.ghostAnchor && (
            <TeleportGhost
              ghostCells={teleport.state.ghostCells}
              anchor={teleport.state.ghostAnchor}
              isValid={teleport.isPlacementValid(teleport.state.ghostAnchor)}
              unitImage={imagesLoaded ? images.unitOn : null}
            />
          )}


          {/* autoTie 카운팅 바운스: 표시등에 숫자, 그리드 중앙에 "십" */}
          {autoTieBounce && (() => {
            const sideImg = images?.sideFull ?? null
            const displayText = String(autoTieBounceCount ?? '')
            return (
              <Group key={`autotie-bounce-${autoTieBounce.x}-${autoTieBounce.y}`}>
                {/* 바운스 스케일 중 아래 표시등이 보이지 않도록 full 배경 고정 */}
                {sideImg ? (
                  <KonvaImage x={autoTieBounce.x} y={autoTieBounce.y} width={SIDE_SIZE} height={SIDE_SIZE} image={sideImg} listening={false} />
                ) : (
                  <Rect x={autoTieBounce.x} y={autoTieBounce.y} width={SIDE_SIZE} height={SIDE_SIZE} fill={COLORS.sideFull} cornerRadius={3} listening={false} />
                )}
                <Text
                  x={autoTieBounce.x} y={autoTieBounce.y} width={SIDE_SIZE} height={SIDE_SIZE}
                  text={displayText}
                  fontSize={18} fontStyle="bold" fontFamily="CookeiRunBold"
                  fill="#FFFFFF" align="center" verticalAlign="middle" listening={false}
                />
                <BounceEffect
                  x={autoTieBounce.x} y={autoTieBounce.y} size={SIDE_SIZE}
                >
                  {sideImg ? (
                    <KonvaImage x={0} y={0} width={SIDE_SIZE} height={SIDE_SIZE} image={sideImg} listening={false} />
                  ) : (
                    <Rect x={0} y={0} width={SIDE_SIZE} height={SIDE_SIZE} fill={COLORS.sideFull} cornerRadius={3} listening={false} />
                  )}
                  <Text
                    x={0} y={0} width={SIDE_SIZE} height={SIDE_SIZE}
                    text={displayText}
                    fontSize={18} fontStyle="bold" fontFamily="CookeiRunBold"
                    fill="#FFFFFF" align="center" verticalAlign="middle" listening={false}
                  />
                </BounceEffect>
              </Group>
            )
          })()}

          {/* autoTie: 묶인 셀 중앙에 "십" 바운스 */}
          {autoTieBundleCenter && (
            <BounceEffect x={autoTieBundleCenter.x - 20} y={autoTieBundleCenter.y - 20} size={40}>
              <Circle x={20} y={20} radius={20} fill="rgba(0,0,0,0.5)" />
              <Text
                x={0} y={0} width={40} height={40}
                text="십" fontSize={22} fontStyle="bold" fontFamily="CookeiRunBold"
                fill="#FFFFFF" align="center" verticalAlign="middle" listening={false}
              />
            </BounceEffect>
          )}

          {/* 정답 애니메이션: 표시등 카운팅 바운스 (숫자만) */}
          {answerMergedBounce && (() => {
            const sideImg = images?.sideFull ?? null
            const displayText = String(answerBounceCount ?? '')
            return (
              <Group key={`counting-bounce-${answerMergedBounce.x}-${answerMergedBounce.y}`}>
                {sideImg ? (
                  <KonvaImage x={answerMergedBounce.x} y={answerMergedBounce.y} width={SIDE_SIZE} height={SIDE_SIZE} image={sideImg} listening={false} />
                ) : (
                  <Rect x={answerMergedBounce.x} y={answerMergedBounce.y} width={SIDE_SIZE} height={SIDE_SIZE} fill={COLORS.sideFull} cornerRadius={3} listening={false} />
                )}
                <Text
                  x={answerMergedBounce.x} y={answerMergedBounce.y} width={SIDE_SIZE} height={SIDE_SIZE}
                  text={displayText}
                  fontSize={18} fontStyle="bold" fontFamily="CookeiRunBold"
                  fill="#FFFFFF" align="center" verticalAlign="middle" listening={false}
                />
                <BounceEffect
                  x={answerMergedBounce.x} y={answerMergedBounce.y} size={SIDE_SIZE}
                >
                  {sideImg ? (
                    <KonvaImage x={0} y={0} width={SIDE_SIZE} height={SIDE_SIZE} image={sideImg} listening={false} />
                  ) : (
                    <Rect x={0} y={0} width={SIDE_SIZE} height={SIDE_SIZE} fill={COLORS.sideFull} cornerRadius={3} listening={false} />
                  )}
                  <Text
                    x={0} y={0} width={SIDE_SIZE} height={SIDE_SIZE}
                    text={displayText}
                    fontSize={18} fontStyle="bold" fontFamily="CookeiRunBold"
                    fill="#FFFFFF" align="center" verticalAlign="middle" listening={false}
                  />
                </BounceEffect>
              </Group>
            )
          })()}

          {/* 정답 애니메이션: 뚱십 그룹 중앙에 "십" 바운스 */}
          {answerBundleCenter && (
            <BounceEffect x={answerBundleCenter.x - 20} y={answerBundleCenter.y - 20} size={40}>
              <Circle x={20} y={20} radius={20} fill="rgba(0,0,0,0.5)" />
              <Text
                x={0} y={0} width={40} height={40}
                text="십" fontSize={22} fontStyle="bold" fontFamily="CookeiRunBold"
                fill="#FFFFFF" align="center" verticalAlign="middle" listening={false}
              />
            </BounceEffect>
          )}

          {/* 묶음 테두리는 doneGroups로 상시 렌더링 (위에서 처리) */}

          {/* 정답 애니메이션: 낱개 바운스 + 숫자 오버레이 */}
          {answerAnimActive && activeBounceIds.map(idx => {
            const item = answerAnimItemsRef.current[idx]
            if (!item) return null
            const unitImg = images?.unitOn ?? null
            const num = looseUnitNumbers.get(idx)
            const numImg = num != null ? (images?.[`num${num}Off` as keyof typeof images] ?? null) : null
            return (
              <BounceEffect key={`bounce-${idx}`} x={item.x} y={item.y} size={CELL_SIZE}>
                {unitImg ? (
                  <KonvaImage x={0} y={0} width={CELL_SIZE} height={CELL_SIZE} image={unitImg} listening={false} />
                ) : (
                  <Rect x={0} y={0} width={CELL_SIZE} height={CELL_SIZE} fill={COLORS.unitOn} cornerRadius={4} />
                )}
                {numImg && (
                  <KonvaImage x={0} y={0} width={CELL_SIZE} height={CELL_SIZE} image={numImg} listening={false} />
                )}
                {num != null && !numImg && (
                  <Text
                    x={0} y={0} width={CELL_SIZE} height={CELL_SIZE}
                    text={String(num)} fontSize={24} fontStyle="bold" fontFamily="CookeiRunBold"
                    fill="#FFFFFF" align="center" verticalAlign="middle" listening={false}
                  />
                )}
              </BounceEffect>
            )
          })}

          {/* 정답 애니메이션: 바운스 완료된 낱개에 숫자 유지 */}
          {looseUnitNumbers.size > 0 && Array.from(looseUnitNumbers.entries()).map(([idx, num]) => {
            if (activeBounceIds.includes(idx)) return null
            const item = answerAnimItemsRef.current[idx]
            if (!item) return null
            const numImg = images?.[`num${num}Off` as keyof typeof images] ?? null
            return (
              <Group key={`unit-num-${idx}`}>
                {numImg ? (
                  <KonvaImage x={item.x} y={item.y} width={CELL_SIZE} height={CELL_SIZE} image={numImg} listening={false} />
                ) : (
                  <>
                    <Rect x={item.x} y={item.y} width={CELL_SIZE} height={CELL_SIZE} fill="rgba(0,0,0,0.5)" cornerRadius={4} />
                    <Text
                      x={item.x} y={item.y} width={CELL_SIZE} height={CELL_SIZE}
                      text={String(num)} fontSize={24} fontStyle="bold" fontFamily="CookeiRunBold"
                      fill="#FFFFFF" align="center" verticalAlign="middle" listening={false}
                    />
                  </>
                )}
              </Group>
            )
          })}

          {/* Bank */}
          <Bank
            grid={grid.bankGrid}
            images={imagesLoaded ? images : null}
            onCellMouseDown={features.bank ? handleBankMouseDown : undefined}
            onCellMouseEnter={features.bank ? handleBankMouseEnter : undefined}
            onCellMouseUp={features.bank ? handleBankMouseUp : undefined}
            disabled={!features.bank}
          />

          {/* Drag overlay */}
          <DragOverlay
            rect={drag.session.rect}
            mode={effectiveDragMode}
            active={drag.session.active}
            target={drag.session.target}
            cellCount={dragCellCount}
          />

          {/* Bank selection persistent overlay (꺼내기 후 유지) */}
          {bankSelection && !drag.session.active && (
            <DragOverlay
              rect={bankSelection.rect}
              mode="borrow"
              active={true}
              target="bank"
              cellCount={bankSelection.count}
            />
          )}

          {/* Tutorial highlight (Konva) */}
          {tutorial.isActive && tutorial.currentStep && (
            <TutorialHighlight
              area={tutorial.currentStep.highlightArea}
              stepPassed={tutorial.stepPassed}
            />
          )}

          {/* 피드백 토스트 (Konva, 상단 중앙) */}
          {feedback && (() => {
            const toastW = Math.min(baseWidth - 40, Math.max(160, feedback.message.length * 16 + 50))
            const toastH = 36
            const toastX = (baseWidth - toastW) / 2
            const toastY = 10
            const bgColor = feedback.isCorrect ? '#E8F5E9' : '#FFF3E0'
            const borderColor = feedback.isCorrect ? '#4CAF50' : '#FF9800'
            const textColor = feedback.isCorrect ? '#2E7D32' : '#E65100'
            return (
              <Group onClick={() => setFeedback(null)} onTap={() => setFeedback(null)}>
                <Rect
                  x={toastX} y={toastY}
                  width={toastW} height={toastH}
                  fill={bgColor} stroke={borderColor} strokeWidth={2}
                  cornerRadius={18}
                  shadowColor="rgba(0,0,0,0.15)" shadowBlur={8} shadowOffsetY={2}
                />
                <Text
                  x={toastX} y={toastY}
                  width={toastW} height={toastH}
                  text={feedback.message}
                  fontSize={18} fontStyle="bold" fontFamily="CookeiRunBold"
                  fill={textColor} align="center" verticalAlign="middle"
                  listening={false}
                />
              </Group>
            )
          })()}

          {/* Tutorial panel (Konva, 은행 하단) */}
          {tutorial.isActive && tutorial.currentStep && tutorial.currentModule && (
            <TutorialPanel
              x={TUTORIAL_OFFSET_X}
              y={TUTORIAL_OFFSET_Y}
              width={TUTORIAL_WIDTH}
              instruction={tutorial.currentStep.instruction}
              hint={tutorial.currentStep.hint}
              showHint={tutorial.showHint}
              stepPassed={tutorial.stepPassed}
              moduleName={tutorial.currentModule.name}
              progress={`${tutorial.completedSteps + 1} / ${tutorial.totalSteps}`}
              completed={completed}
              onStop={tutorial.stop}
            />
          )}
        </Layer>
      </Stage>

      </div>
      </div>

    </div>
  )
}
